/**
 * Glass-styled grid list for the learner experience.
 * Wraps React Aria GridList — supports navigation mode (click → go somewhere)
 * and selection mode (toggle items for batch actions).
 *
 * Visual: leading badge, title + subtitle, optional trailing text, chevron/checkbox.
 * Renders dividers between items automatically.
 */
import { type ReactNode, type Key } from 'react';
import { Surface } from './Surface';
import {
  GridList as AriaGridList,
  GridListItem as AriaGridListItem,
  Checkbox as AriaCheckbox,
  type Selection,
} from 'react-aria-components';
import { ChevronRight, Check } from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface GridListItem {
  id: string;
  /** Short text for the leading badge (e.g. "L4", "OC", "3") */
  leading?: string;
  /** Icon to show instead of leading text */
  leadingIcon?: ReactNode;
  /** Colour for leading badge background tint + text */
  leadingColor?: string;
  /** Primary label */
  title: string;
  /** Secondary line below title */
  subtitle?: string;
  /** Right-aligned metadata (e.g. "85%", "In 18h") */
  trailing?: string;
  /** Whether the item is disabled */
  isDisabled?: boolean;
}

interface GridListBaseProps {
  /** Items to render */
  items: GridListItem[];
  /** Additional className on the outer container */
  className?: string;
  /** Additional style on the outer container */
  style?: React.CSSProperties;
  /** Whether to render the glass surface wrapper */
  surface?: boolean;
}

interface GridListNavigationProps extends GridListBaseProps {
  /** Navigation mode — called when an item is activated (click / Enter) */
  onAction: (id: string) => void;
  selectionMode?: never;
  selectedKeys?: never;
  onSelectionChange?: never;
}

interface GridListSelectionProps extends GridListBaseProps {
  /** Selection mode */
  selectionMode: 'single' | 'multiple';
  /** Controlled selected keys */
  selectedKeys?: Selection;
  /** Selection change handler */
  onSelectionChange?: (keys: Selection) => void;
  onAction?: never;
}

export type GridListProps = GridListNavigationProps | GridListSelectionProps;

// ─── Component ───────────────────────────────────────────────────────────────

export function GridList({
  items,
  onAction,
  selectionMode,
  selectedKeys,
  onSelectionChange,
  className,
  style,
  surface = true,
}: GridListProps) {
  const isSelection = !!selectionMode;

  const list = (
    <AriaGridList
      aria-label="List"
      selectionMode={selectionMode ?? 'none'}
      selectedKeys={selectedKeys}
      onSelectionChange={onSelectionChange}
      onAction={onAction ? (key: Key) => onAction(String(key)) : undefined}
      className={surface ? undefined : className}
      style={surface ? undefined : style}
    >
      {items.map((item, i) => (
        <AriaGridListItem
          key={item.id}
          id={item.id}
          textValue={item.title}
          isDisabled={item.isDisabled}
          style={{
            borderRadius: 0,
            cursor: item.isDisabled ? 'not-allowed' : 'pointer',
            transition: 'background 0.15s',
            outline: 'none',
            opacity: item.isDisabled ? 0.5 : 1,
            fontFamily: 'var(--font-sans)',
            boxShadow: i > 0 ? 'inset 0 1px 0 var(--color-surface-border)' : undefined,
          }}
          className="hover:bg-[var(--color-surface-hover)] focus-visible:bg-[var(--color-surface-hover)] data-[selected]:bg-[var(--color-accent-wash)]"
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 14,
              padding: '12px 16px',
            }}
          >
            {/* Selection checkbox */}
            {isSelection && (
              <AriaCheckbox
                slot="selection"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 20,
                  height: 20,
                  borderRadius: 6,
                  border: '1.5px solid var(--color-border-strong)',
                  background: 'transparent',
                  flexShrink: 0,
                  cursor: 'pointer',
                  transition: 'border-color 0.15s, background 0.15s',
                }}
                className="data-[selected]:bg-[var(--color-accent)] data-[selected]:border-[var(--color-accent)]"
              >
                {({ isSelected }) =>
                  isSelected ? (
                    <Check
                      size={13}
                      strokeWidth={2.5}
                      style={{ color: 'var(--color-text-on-accent)' }}
                    />
                  ) : null
                }
              </AriaCheckbox>
            )}

            {/* Leading badge */}
            {(item.leading || item.leadingIcon) && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 34,
                  height: 34,
                  borderRadius: 8,
                  background: item.leadingColor
                    ? `color-mix(in srgb, ${item.leadingColor} 10%, transparent)`
                    : 'var(--color-surface-muted)',
                  flexShrink: 0,
                  fontSize: 13,
                  fontWeight: 700,
                  color: item.leadingColor ?? 'var(--color-text-secondary)',
                  letterSpacing: '-0.01em',
                }}
              >
                {item.leadingIcon ?? item.leading}
              </div>
            )}

            {/* Title + subtitle */}
            <div style={{ minWidth: 0, flex: '1 1 0' }}>
              <p
                style={{
                  fontSize: 16,
                  fontWeight: 600,
                  letterSpacing: '-0.015em',
                  color: 'var(--color-text)',
                  lineHeight: 1.3,
                }}
              >
                {item.title}
              </p>
              {item.subtitle && (
                <p
                  style={{
                    fontSize: 13,
                    fontWeight: 400,
                    color: 'var(--color-text-muted)',
                    marginTop: 1,
                  }}
                >
                  {item.subtitle}
                </p>
              )}
            </div>

            {/* Trailing text */}
            {item.trailing && (
              <span
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: 'var(--color-text-secondary)',
                  flexShrink: 0,
                }}
              >
                {item.trailing}
              </span>
            )}

            {/* Chevron (navigation mode only) */}
            {!isSelection && (
              <ChevronRight
                size={13}
                strokeWidth={2}
                style={{ color: 'var(--color-text-muted)', opacity: 0.5, flexShrink: 0 }}
              />
            )}
          </div>
        </AriaGridListItem>
      ))}
    </AriaGridList>
  );

  if (surface) {
    return (
      <Surface className={className} style={style}>
        {list}
      </Surface>
    );
  }

  return list;
}
